import { sendPostMessage } from 'helpers/post-message.helper';
import { LANGUAGES } from 'constants/languages';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { CUSTOM_BRANDING, LIVE_TRANSLATIONS, MODIFY_MENU, WHITE_LABEL } from 'constants/constants';
import {
  ACCESSIBILITY_MENU_DEFAULT_PATTERN,
  ACCESSIBILITY_MENU_PAID_FEATURES,
  ACCESSIBILITY_MENU_PAID_PATTERN,
  ACCESSIBILITY_MENU_PROFILES_PATTERN,
} from 'constants/accessibility-menu-patterns';
import { colorsHelper, colourIsLight } from 'helpers/colors.helper';
import { createContext } from 'preact';
import { useApi } from 'services/use-api';
import uniq from 'lodash/uniq';
import { ALLY_FEATURES as ALLY_FEATURES_V1 } from 'constants/ally-features';
import { ALLY_FEATURES as ALLY_FEATURES_V2 } from 'constants-v2/ally-features';
import { sessionStore } from 'global/session.store';
import { readerInteractionStore } from 'global/reader-interaction.store';
import { useWidgetState } from 'services/use-widget-state';
import { includes } from 'helpers';
import { filterActiveFeatures, filterActiveFeaturesPattern } from '../helpers/features.helper';

export const ApplicationContext = createContext<ReturnType<typeof useApplicationSetup>>(null);

export const useApplicationSetup = (isNewDesign?: boolean) => {
  const Api = useApi();
  const config = useRef<any>({});
  const [widgetBlocked, setWidgetBlocked] = useState(false);
  const [userSpecificPosition, setUserSpecificPosition] = useState<number>(null);
  const [position, setPosition] = useState<number>(null);

  const [widgetPageLang, setWidgetPageLang] = useState<string>(null);
  const [language, setLanguage] = useState<string>(null);
  const [href, setHref] = useState<string>(null);
  const [code, setCode] = useState<string>(null);

  const [partner, setPartner] = useState<string>(null);

  const ALLY_FEATURES = useMemo(() => (isNewDesign ? ALLY_FEATURES_V2 : ALLY_FEATURES_V1), [
    isNewDesign,
  ]);

  const paidFeatures = useMemo(
    () => filterActiveFeatures(ACCESSIBILITY_MENU_PAID_FEATURES, ALLY_FEATURES),
    [isNewDesign],
  );

  const profilesPattern = useMemo(() => (isNewDesign ? ACCESSIBILITY_MENU_PROFILES_PATTERN : ''), [
    isNewDesign,
  ]);

  const defaultPattern = useMemo(
    () => filterActiveFeaturesPattern(ACCESSIBILITY_MENU_DEFAULT_PATTERN, ALLY_FEATURES),
    [isNewDesign],
  );

  const paidPattern = useMemo(
    () => filterActiveFeaturesPattern(ACCESSIBILITY_MENU_PAID_PATTERN, ALLY_FEATURES),
    [isNewDesign],
  );

  const [previewCustomLogo, setPreviewCustomLogo] = useState<{
    path: string;
    link: string;
  }>(null);

  const [areResourcesLoaded, setResourcesLoaded] = useState<boolean>(false);

  const [accessibilityStatement, setAccessibilityStatement] = useState<{
    text?: string;
    link?: string;
    enabled?: boolean;
  }>(null);

  const [tunings, setTunings] = useState<TuningConfig>(null);
  const [accountIdCode, setAccountIdCode] = useState<string>(null);
  const [targetPageOrigin, setTargetPageOrigin] = useState<string>(null);
  const [noManage, setNoManage] = useState<boolean>(null);
  const [noLogo, setNoLogo] = useState<boolean>(null);
  const [thisSiteId, setThisSiteId] = useState<string>(null);
  const [orgInfo, setOrgInfo] = useState<OrgInfo>(null);
  const [tuningsFromServerInProgress, setTuningsFromServerInProgress] = useState(true);

  const [whiteLabelOptions, setWhiteLabelOptions] = useState<WhiteLabelOptions>(null);
  const [customBrandingOptions, setCustomBrandingOptions] = useState({
    custom_logo_image_path: '',
    custom_logo_image_link: '',
    custom_logo_image_link_no_schema: '',
  });

  const customMenuOptions = useRef<{ featuresPattern: string; colorMode?: string }>({
    featuresPattern: defaultPattern,
  });

  const [menuUserLanguageValue, setMenuUserLanguageValue] = useState<{
    languageObj?: { value: string };
    liveLanguageEnabled?: boolean;
  }>({});

  const [liveTranslationsOptions, setLiveTranslationsOptions] = useState({
    is_enabled: false,
    is_renewable: false,
    remainingChars: null,
    translationAllowed: false,
    autoTranslation: false,
  });

  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const settings = useRef<Record<string, { value: number; disabled: boolean }>>(null);
  const enabledMenuFeatures = useRef<string[]>([]);

  const [widgetColors, setWidgetColors] = useState<{
    mainBfColor: string;
    gradient: string;
    isLightColour: boolean;
  }>(null);

  const [paidAi, setPaidAi] = useState(false);
  const [paidProductsEnabled, setPaidProductsEnabled] = useState<PaidProducts>({
    WHITE_LABEL: false,
    CUSTOM_BRANDING: false,
    MODIFY_MENU: false,
    USAGE_STATS: false,
    REMEDIATION: false,
    LIVE_TRANSLATIONS: false,
  });

  const { close, isOpen } = useWidgetState();

  useEffect(() => {
    if (!(document as any).fonts?.ready) {
      setResourcesLoaded(true);
    } else {
      (document as any).fonts.ready.then(() => {
        setResourcesLoaded(true);
      });
    }
  }, []);

  function updateMenuUserLanguageValue({ value, enabled }: { value?: string; enabled?: boolean }) {
    setMenuUserLanguageValue((data) => ({
      ...data,
      ...(value ? { languageObj: LANGUAGES.filter((lang) => lang.value === value)[0] } : {}),
      ...(typeof enabled === 'boolean' ? { liveLanguageEnabled: enabled } : {}),
    }));
  }

  const modifyMenu = (payload, isPaidAi) => {
    setPaidProductsEnabled((values) => ({ ...values, MODIFY_MENU: true }));
    applyCustomMenu(payload, isPaidAi);
  };

  // TODO add logic for profiles when backend will be ready
  const checkProfilesPattern = (featuresPattern: string[]) => {
    const profiles = profilesPattern.split('|');
    const isProfilePresent = featuresPattern.find((feature) => includes(profiles, feature));

    return isProfilePresent ? featuresPattern : featuresPattern.concat(profiles);
  };

  function applyCustomMenu(payload, paidAi: boolean) {
    const receivedFeatures = payload.features_pattern
      .split('|')
      .filter((el) => !!ALLY_FEATURES[el]);

    // filter out paid features if widget plan is "free"
    const availablePattern = paidAi
      ? checkProfilesPattern(receivedFeatures)
      : receivedFeatures.filter((feature) => !includes(paidFeatures, feature));

    customMenuOptions.current = {
      featuresPattern: uniq(availablePattern).join('|'),
      colorMode: payload.color_mode,
    };
  }

  function getTuningsFromServer(accountIdCode: string, origin: string, platfAppInstalledSiteId: string, pageUrl: string) {
    Api.post(`tunings/${accountIdCode}`, {
      origin,
      platfAppInstalledSiteId,
      pageUrl,
    })
      .then((response) => {
        if (response.data) {
          updateUserServicesInfo(response.data.services, response.data.remediation);
          setOrgInfo(response.data.orgInfo);
        }
      })
      .catch((e) => console.error(e))
      .finally(() => setTuningsFromServerInProgress(false));
  }

  const updateWhiteLabelOptions = (data: {
    hide_report: boolean;
    hide_manage: boolean;
    hide_logo: boolean;
    hide_asterisk: boolean;
  }) => {
    setPaidProductsEnabled((values) => ({ ...values, WHITE_LABEL: true }));
    setWhiteLabelOptions({
      wlHideReport: data.hide_report,
      wlHideManage: data.hide_manage,
      wlHideLogo: data.hide_logo,
      wlHideAsterisk: data.hide_asterisk,
    });
  };

  const updateCustomBrandingOptions = (data: {
    custom_logo_image_path: string;
    custom_logo_image_link: string;
  }) => {
    setPaidProductsEnabled((values) => ({ ...values, CUSTOM_BRANDING: true }));
    setCustomBrandingOptions({
      custom_logo_image_path: data.custom_logo_image_path,
      custom_logo_image_link: data.custom_logo_image_link,
      custom_logo_image_link_no_schema: data.custom_logo_image_link
        ? data.custom_logo_image_link.replace(/(http|https):\/\//g, '')
        : null,
    });
  };

  const updateLiveTranslationsOptions = (data: {
    is_enabled: boolean;
    is_renewable: boolean;
    remainingChars: number;
    translationAllowed: boolean;
    autoTranslation: boolean;
  }) => {
    setPaidProductsEnabled((values) => ({ ...values, LIVE_TRANSLATIONS: true }));
    setLiveTranslationsOptions(data);
  };

  function updateUserServicesInfo(services, remediation) {
    if (services) {
      setNoManage(services.noManage);
      setNoLogo(services.noLogo);
      setThisSiteId(services.siteId);

      if (services[WHITE_LABEL]) {
        const srv = services[WHITE_LABEL];
        updateWhiteLabelOptions(srv);
      }
      if (services[CUSTOM_BRANDING]) {
        const srv = services[CUSTOM_BRANDING];
        updateCustomBrandingOptions(srv);
      }
      if (services[LIVE_TRANSLATIONS]) {
        const srv = services[LIVE_TRANSLATIONS];
        updateLiveTranslationsOptions(srv);
      }

      // todo rework this. Info about paid service must be in "services" obj
      if (!!remediation && !!remediation.alt && remediation.alt.enabled) {
        setPaidProductsEnabled((values) => ({ ...values, REMEDIATION: true }));
      }
    }
  }

  const checkLightColor = (color: string) => {
    const colorsForCheck = colorsHelper(color.replace('#', ''));
    return colourIsLight(colorsForCheck[0], colorsForCheck[1], colorsForCheck[2]);
  };

  function refreshAccessibilityMenuConfig(serverSettings, isPaidAi: boolean) {
    if (!serverSettings) {
      return;
    }

    const menuFeatures = customMenuOptions.current.featuresPattern
      .split('|')
      .filter((f) => !!f.trim() && !!ALLY_FEATURES[f]);

    const availableFeatures = isPaidAi
      ? menuFeatures
      : menuFeatures.filter((feature) => !includes(paidFeatures, feature));

    settings.current = availableFeatures.reduce((updSettings, featureName) => {
      updSettings[`userway-${featureName}`] = serverSettings[`userway-${featureName}`];

      if (featureName === 's2') {
        updSettings['userway-s10'] = serverSettings['userway-s10'];
        updSettings['userway-s16'] = serverSettings['userway-s16'];
      }

      if (featureName === 's7') {
        updSettings['userway-s15'] = serverSettings['userway-s15'];
      }

      return updSettings;
    }, {});

    setSettingsLoaded(true);
    enabledMenuFeatures.current = availableFeatures;
  }

  const getInitialDataFromUrl = () => {
    const queryString = window.location.search;
    const decodedParams = decodeURIComponent(queryString);
    const initialConfigKeys = [
      'services',
      'tunings',
      'language',
      'account',
      'widgetPageLang',
    ];
    const getQueryParams = (dataString: string) => {
      dataString = dataString.split('+').join(' ');

      const params = {};
      const re = /[?&]?([^=]+)=([^&]*)/g;
      let tokens;

      while ((tokens = re.exec(dataString))) {
        params[decodeURIComponent(tokens[1])] = decodeURIComponent(tokens[2]);
      }

      return params;
    };

    const urlParams = getQueryParams(decodedParams);

    const getDataFormUrlByKey = (key) => {
      const params = urlParams[key];
      return !params ? null : JSON.parse(params);
    };

    const data = initialConfigKeys.reduce(
      (result, key) => ({ ...result, [key]: getDataFormUrlByKey(key) }),
      {},
    );
    receiveConfig(data || {});
  };

  const init = () => {
    getInitialDataFromUrl();

    sendPostMessage({
      action: 'getConfig',
    });

    window.addEventListener('message', (event) => {
      const data = event.data || {};

      if (data.action === 'sendConfig') {
        receiveConfig(data.config || {});
      } else if (data.action === 'setWidgetColour') {
        const color = data.config?._userway_config?.color || data.payload?.color || null;
        const gradient = data.config?._userway_config?.gradient || data.payload?.gradient || null;

        setWidgetColors({
          mainBfColor: color || '#003d68',
          gradient,
          isLightColour: color ? checkLightColor(color) : false,
        });
      } else if (data.action === 'on_demand_reader_end') {
        readerInteractionStore.internalReaderStopped$.next();
      }
    });
  };

  const receiveConfig = (data) => {
    config.current = data || {};
    setAccountIdCode(data.account);
    updateUserServicesInfo(data.services, null);

    if (data.account && data.origin) {
      setTargetPageOrigin(data.origin);

      const platfAppInstalledSiteId = data._userway_config?.platfAppInstalledSiteId || null;

      getTuningsFromServer(data.account, data.origin, platfAppInstalledSiteId, data.href);
    }

    sessionStore.refreshWidget({ code: data.account });

    let currentTunings;

    if (data.tunings) {
      currentTunings = data.tunings;
      setTunings(currentTunings);
      if (
        currentTunings.widget_blocked_for_site &&
        currentTunings.widget_blocked_for_site === 'on'
      ) {
        setWidgetBlocked(true);
      }
    }

    if (data.partner) {
      setPartner(data.partner);
    }

    const hardcodedPos = data.position;
    const userSpecificPos = data.userSpecificPosition;
    // let currentPosition = 1;

    if (userSpecificPos) {
      setUserSpecificPosition(+userSpecificPos);
      // currentPosition = userSpecificPos;
    }

    if (hardcodedPos) {
      setPosition(+hardcodedPos);
      // if (userSpecificPosition) {
      //     currentPosition = hardcodedPos;
      // }
    }

    // if (currentTunings && currentTunings.widget_position && !userSpecificPos && !hardcodedPos) {
    //     currentPosition = currentTunings.widget_position;
    // }

    const currentLanguage = data.language;
    setLanguage(currentLanguage);
    setWidgetPageLang(data.widgetPageLang);

    setHref(data.href);

    setCode(data?.account);

    let siteNameAccessibilityStatement = null;
    if (
      (data.statement_url || data.tunings.accessibility_url) &&
      !data.tunings.accessibility_link_text &&
      !data.statement_text &&
      data.tunings.site_name
    ) {
      siteNameAccessibilityStatement = 'widget.footer.statement.label';
    }

    setAccessibilityStatement(() => {
      const resultAccessibilityStatement: {
        text?: string;
        link?: string;
        enabled?: boolean;
      } = {};

      resultAccessibilityStatement.text =
        data.statement_text ||
        data.tunings.accessibility_link_text ||
        siteNameAccessibilityStatement;
      resultAccessibilityStatement.link = data.statement_url || data.tunings.accessibility_url;
      if (resultAccessibilityStatement.link && !resultAccessibilityStatement.link.match('^http')) {
        resultAccessibilityStatement.link = `http://${resultAccessibilityStatement.link}`;
      }
      const statementHardcoded = data.statement_url && data.statement_text;
      resultAccessibilityStatement.enabled =
        statementHardcoded ||
        (resultAccessibilityStatement.text &&
          resultAccessibilityStatement.link &&
          (!data.tunings.accessibility_link_enabled ||
            data.tunings.accessibility_link_enabled === 'on'));

      return resultAccessibilityStatement;
    });

    // Note, we set MODIFY_MENU here, not in updateUserServicesInfo to avoid menu jumping
    const { services } = data;
    let isPaidAi;

    if (services && services[MODIFY_MENU]) {
      isPaidAi = services.paidAi || !services.hasOwnProperty('paidAi');
      modifyMenu(services[MODIFY_MENU], isPaidAi);
    } else if (services && (services.paidAi || !services.hasOwnProperty('paidAi'))) {
      isPaidAi = true;
      customMenuOptions.current.featuresPattern = paidPattern;
    }

    setPaidAi(isPaidAi);

    setMenuUserLanguageValue(() => ({
      liveLanguageEnabled:
        isPaidAi &&
        (!currentTunings.hasOwnProperty('widget_live_language_enabled') ||
          currentTunings.widget_live_language_enabled),
      languageObj: LANGUAGES.filter((el) => el.value === currentLanguage)[0],
    }));

    refreshAccessibilityMenuConfig(data.settings, isPaidAi);
    const color = config.current._userway_config?.color || currentTunings?.widget_color || null;
    const gradient =
      config.current._userway_config?.gradient || currentTunings?.widget_color_gradient || null;

    setWidgetColors({
      mainBfColor: color || '#003d68',
      gradient,
      isLightColour: color ? checkLightColor(color) : false,
    });
  };

  const sendEvent = () => {
    sendPostMessage({
      action: 'setProperties',
      settings: settings.current,
      pattern: enabledMenuFeatures.current,
    });
  };

  const soundEffectsEnabled = useMemo(
    () => tunings?.hasOwnProperty('widget_sounds') && tunings?.widget_sounds,
    [tunings],
  );

  useEffect(() => {
    init();
  }, []);

  return {
    config,
    tunings,
    setTunings,
    settings,
    settingsLoaded,

    tuningsFromServerInProgress,

    accountIdCode,
    targetPageOrigin,

    widgetBlocked,
    widgetColors,
    whiteLabelOptions,
    updateWhiteLabelOptions,
    customBrandingOptions,
    updateCustomBrandingOptions,
    customMenuOptions,
    enabledMenuFeatures,
    modifyMenu,
    liveTranslationsOptions,
    updateLiveTranslationsOptions,

    userSpecificPosition,
    setUserSpecificPosition,

    position,

    menuUserLanguageValue,
    updateMenuUserLanguageValue,
    setMenuUserLanguageValue,
    language,
    widgetPageLang,
    setLanguage,
    href,
    code,
    thisSiteId,
    orgInfo,

    accessibilityStatement,

    soundEffectsEnabled,

    sendEvent,
    isOpen,
    close,

    paidProductsEnabled,
    setPaidProductsEnabled,
    paidAi,
    setPaidAi,
    noManage,
    noLogo,
    previewCustomLogo,
    setPreviewCustomLogo,
    setAccessibilityStatement,
    areResourcesLoaded,
    refreshAccessibilityMenuConfig,
    isNewDesign,
    paidFeatures,
    defaultPattern,
    paidPattern,
    partner,
  };
};
